import { computed, type ComputedRef } from "vue";
import { sortSceneChips } from "../lib/sortSceneChips";
import { just, nothing, unwrapMaybeOr } from "../lib/maybe";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useSceneOpeningsStore } from "../stores/useSceneOpeningsStore";
import { storeToRefs } from "pinia";
import { useTimelineMapsStore } from "../stores/useTimelineMapsStore";
import {
  buildStoryboardArcIndex,
  storyboardArcChipsForScene,
} from "../lib/storyboardArcs";
import { timelineCharacterChips } from "../lib/timelineCharacters";
import { timelineEntityChips } from "../lib/timelineEntities";
import {
  orderMapLocationChips,
  type StoryboardMapLocationChip,
} from "../lib/storyboardMapLocations";
import type { StoryboardArcChip } from "../lib/storyboardArcs";
import type { TimelineCharacterChip } from "../lib/timelineCharacters";
import type { StoryboardEntityChip } from "../components/storyboard/SceneMetadata.vue";

const absentMapFields = {
  mapFile: nothing,
  landmarkId: nothing,
  iconId: nothing,
  previewSrc: nothing,
} as const;

export function mapLocationChipAsEntity(
  chip: StoryboardMapLocationChip,
): StoryboardEntityChip {
  return {
    id: chip.id,
    label: chip.label,
    kind: "map",
    entryFile: nothing,
    momentId: nothing,
    manuallyLinked: false,
    mapLinked: false,
    color: nothing,
    mapFile: just(chip.mapFile),
    landmarkId: just(chip.landmarkId),
    iconId: just(chip.iconId),
    previewSrc: chip.previewSrc,
  };
}

function entityLinkAsChip(chip: {
  id: string;
  label: string;
  entryFile: string;
  color: StoryboardEntityChip["color"];
  manuallyLinked: boolean;
  mapLinked: boolean;
}): StoryboardEntityChip {
  return {
    id: chip.id,
    label: chip.label,
    kind: "entity",
    entryFile: just(chip.entryFile),
    momentId: nothing,
    manuallyLinked: chip.manuallyLinked,
    mapLinked: chip.mapLinked,
    color: chip.color,
    ...absentMapFields,
  };
}

function arcAsChip(chip: StoryboardArcChip): StoryboardEntityChip {
  return {
    id: chip.id,
    label: chip.label,
    kind: "arc",
    entryFile: just(chip.entryFile),
    momentId: just(chip.momentId),
    manuallyLinked: false,
    mapLinked: false,
    color: chip.color,
    ...absentMapFields,
  };
}

function characterAsChip(chip: TimelineCharacterChip): StoryboardEntityChip {
  return {
    id: chip.id,
    label: chip.label,
    kind: "character",
    entryFile: just(chip.entryFile),
    momentId: nothing,
    manuallyLinked: false,
    mapLinked: false,
    color: chip.color,
    ...absentMapFields,
  };
}

/** Shared scene links; the timeline may separately suppress repeated map previews. */
export function useSceneMetadata(): {
  chipsFor: (path: string) => StoryboardEntityChip[];
  ready: ComputedRef<boolean>;
} {
  const session = useEditorSessionStore();
  const entities = useOutlineEntitiesStore();
  const openings = useSceneOpeningsStore();
  const { index, ready } = storeToRefs(useTimelineMapsStore());
  const arcs = computed(() =>
    buildStoryboardArcIndex(entities.databaseEntryFields),
  );
  const mapOrder = computed(() =>
    session.manuscript.flatMap((item) =>
      item.type === "map" ? [item.mapPath.replace(/^Database\//, "")] : [],
    ),
  );

  const entryTypes = computed(
    () =>
      new Map(
        entities.databaseEntryFields.map((entry) => [
          entry.name,
          unwrapMaybeOr(entry.fields.type, "other"),
        ]),
      ),
  );

  function chipsFor(path: string): StoryboardEntityChip[] {
    const arcChips = storyboardArcChipsForScene(path, arcs.value);
    const charactersWithMoments = new Set(
      arcChips.map((chip) => chip.entryFile),
    );
    const places = index.value.get(path) ?? [];
    return sortSceneChips<StoryboardEntityChip>(
      [
        ...orderMapLocationChips(places, mapOrder.value).map(
          mapLocationChipAsEntity,
        ),
        ...timelineEntityChips(path, entities.databaseEntryFields, places).map(
          entityLinkAsChip,
        ),
        ...arcChips.map(arcAsChip),
        ...timelineCharacterChips(
          openings.documentsByPath[path] ?? "",
          entities.databaseEntryFields,
          path,
        )
          .filter((chip) => charactersWithMoments.has(chip.entryFile) === false)
          .map(characterAsChip),
      ],
      entryTypes.value,
    );
  }
  return { chipsFor, ready };
}
